import { Button, Typography } from 'antd';
import type { IconSvgElement } from '@hugeicons/react';
import type { ReactNode } from 'react';
import { Icon } from '../Icon/Icon.js';
import { space, typography } from '@/design/tokens.js';

const { Title, Text } = Typography;

export interface EmptyStateProps {
  icon?: IconSvgElement;
  /** What is not here, in plain words. */
  title: string;
  /** Why it is empty, and what would fill it. */
  description?: string;
  action?: { label: string; onClick: () => void; icon?: IconSvgElement };
  secondaryAction?: ReactNode;
  size?: 'small' | 'default';
}

/**
 * Shown when there is nothing to show.
 *
 * An empty state should tell the user *why* it is empty and what to do next —
 * "No leave applications yet. Apply for leave and it will appear here." beats
 * a grey "No data" every time. Nielsen again: help users recognise and recover,
 * and match the system to the real world.
 *
 * Every list in the product uses this, so "empty" never looks like "broken".
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'default',
}: EmptyStateProps) {
  const compact = size === 'small';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: compact ? `${space[8]}px ${space[4]}px` : `${space[16]}px ${space[6]}px`,
        gap: space[2],
      }}
    >
      {icon ? (
        <span
          aria-hidden
          style={{
            display: 'grid',
            placeItems: 'center',
            width: compact ? 40 : 52,
            height: compact ? 40 : 52,
            borderRadius: 12,
            backgroundColor: 'var(--ant-color-fill-secondary)',
            color: 'var(--ant-color-text-tertiary)',
            marginBottom: space[2],
          }}
        >
          <Icon icon={icon} size={compact ? 'lg' : '2xl'} />
        </span>
      ) : null}

      <Title
        level={3}
        style={{
          margin: 0,
          fontSize: compact ? typography.size.md : typography.size.lg,
          fontWeight: typography.weight.semibold,
        }}
      >
        {title}
      </Title>

      {description ? (
        <Text type="secondary" style={{ maxWidth: 420 }}>
          {description}
        </Text>
      ) : null}

      {action || secondaryAction ? (
        <div
          style={{
            display: 'flex',
            gap: space[2],
            marginTop: space[3],
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {action ? (
            <Button
              type="primary"
              onClick={action.onClick}
              icon={action.icon ? <Icon icon={action.icon} size="sm" /> : undefined}
            >
              {action.label}
            </Button>
          ) : null}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
